import { ReactNode, useCallback, useMemo, useSyncExternalStore } from "react";
import type { MedplumClient, MedplumClientEventMap } from "@medplum/core";
import { reactContext } from "@medplum/react-hooks";

/**
 * The same events Medplum's own provider listens to.
 */
const MEDPLUM_STATE_EVENTS: (keyof MedplumClientEventMap)[] = [
  "change",
  "storageInitialized",
  "storageInitFailed",
  "profileRefreshing",
  "profileRefreshed",
];

/**
 * Matches the default navigate of Medplum's provider.
 */
const assignLocation = (path: string) => {
  window.location.assign(path);
};

type Props = {
  medplum: MedplumClient;
  children: ReactNode;
};

/**
 * Replaces Medplum's `MedplumProvider`. That one reads the client state
 * during render but subscribes in an effect, so an event fired in between
 * is lost and `loading` stays true forever. Under React 19 this happens on
 * a page reload with a stored session. `useSyncExternalStore` re-reads the
 * state after it subscribes, so no event is lost. Remove this once Medplum
 * fixes its provider.
 */
export const MedplumClientProvider: React.FC<Props> = ({
  medplum,
  children,
}) => {
  const subscribe = useCallback(
    (onChange: () => void) => {
      MEDPLUM_STATE_EVENTS.forEach((type) => {
        medplum.addEventListener(type, onChange);
      });

      return () => {
        MEDPLUM_STATE_EVENTS.forEach((type) => {
          medplum.removeEventListener(type, onChange);
        });
      };
    },
    [medplum]
  );
  const profile = useSyncExternalStore(subscribe, () => medplum.getProfile());
  const loading = useSyncExternalStore(subscribe, () => medplum.isLoading());

  const value = useMemo(
    () => ({ medplum, navigate: assignLocation, profile, loading }),
    [medplum, profile, loading]
  );

  return (
    <reactContext.Provider value={value}>{children}</reactContext.Provider>
  );
};
